import React from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const GROUP_LABELS = {
  tshirts: "T-Shirts / Polos", tracks: "Track Pants / Lowers", tracksuits: "Track Suits",
  shorts: "Shorts / 3/4ths", jackets: "Jackets / Hoodies", innerwear: "Innerwear / Activewear",
};

export const SORTS = [
  { id: "newest", label: "Newest first" },
  { id: "price_asc", label: "Price: low to high", gated: true },
  { id: "price_desc", label: "Price: high to low", gated: true },
  { id: "stock", label: "Most in stock", gated: true },
];

function FacetSelect({ value, onChange, items, all, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 rounded-full bg-white text-sm w-full sm:w-44" data-testid={testId}>
        <SelectValue placeholder={all} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="All" data-testid={`${testId}-All`}>{all}</SelectItem>
        {items.map((it) => (
          <SelectItem key={it.name} value={it.name} data-testid={`${testId}-${it.name}`}>
            {it.name} ({it.count})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function CatalogFilters({ f, set, facets, approved, onClearAll }) {
  const chips = [
    ["gender", f.gender], ["category", f.category], ["brand", f.brand], ["size", f.size], ["colour", f.colour],
  ].filter(([, v]) => v && v !== "All");
  if (f.priceMin || f.priceMax) chips.push(["price", `₹${f.priceMin || 0} – ${f.priceMax ? `₹${f.priceMax}` : "any"}`]);
  if (f.newOnly) chips.push(["newOnly", "New arrivals"]);
  if (f.group) chips.push(["group", GROUP_LABELS[f.group] || f.group]);

  const clear = (k) => (k === "price" ? set({ priceMin: "", priceMax: "" }) : set({ [k]: k === "newOnly" ? false : k === "group" ? "" : "All" }));

  return (
    <div className="space-y-3" data-testid="catalog-filters">
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
        <FacetSelect value={f.category} onChange={(v) => set({ category: v, group: "" })} items={facets?.categories || []} all="All categories" testId="filter-category" />
        <FacetSelect value={f.brand} onChange={(v) => set({ brand: v })} items={facets?.brands || []} all="All brands" testId="filter-brand" />
        <FacetSelect value={f.size} onChange={(v) => set({ size: v })} items={facets?.sizes || []} all="All sizes" testId="filter-size" />
        <FacetSelect value={f.colour} onChange={(v) => set({ colour: v })} items={facets?.colours || []} all="All colours" testId="filter-colour" />
        {approved && (
          <div className="col-span-2 flex items-center gap-2">
            <Input type="number" inputMode="numeric" placeholder={`Min ₹${Math.floor(facets?.price?.min || 0)}`} value={f.priceMin}
              onChange={(e) => set({ priceMin: e.target.value })} className="h-10 rounded-full bg-white w-full sm:w-32 text-sm" data-testid="filter-price-min" />
            <span className="text-slate-400 text-sm">–</span>
            <Input type="number" inputMode="numeric" placeholder={`Max ₹${Math.ceil(facets?.price?.max || 0)}`} value={f.priceMax}
              onChange={(e) => set({ priceMax: e.target.value })} className="h-10 rounded-full bg-white w-full sm:w-32 text-sm" data-testid="filter-price-max" />
          </div>
        )}
        <Select value={f.sort} onValueChange={(v) => set({ sort: v })}>
          <SelectTrigger className="h-10 rounded-full bg-slate-900 text-white border-slate-900 text-sm w-full sm:w-48 col-span-2 sm:col-span-1" data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.filter((s) => approved || !s.gated).map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`sort-${s.id}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
          {chips.map(([k, v]) => (
            <button key={k} onClick={() => clear(k)} data-testid={`clear-filter-${k}`}
              className="inline-flex items-center gap-1 rounded-full bg-[#FFDA00]/30 border border-[#FFDA00] px-3 py-1 text-xs font-semibold text-slate-800 hover:bg-[#FFDA00]/50">
              {v} <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={onClearAll}
            className="text-xs font-medium text-slate-500 hover:text-slate-900 underline" data-testid="clear-all-filters">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
